import clsx from 'clsx';

import {
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@/ui';

import FilterDropdown from './FilterDropdown';
import MembersTableRowComponent from './MembersTableRow';
import {
  ALL_MEMBERS_TABLE_COLUMNS,
  ALL_MEMBERS_TABLE_FILTERS,
} from './columns';

import type { MembersTableColumn, MembersTableFilterKey } from './columns';
import type { FilterOption, MemberTableRow } from './types';
import type {
  MembersTableCoachCountFilter,
  MembersTableFacets,
  MembersTableSortBy,
  MembersTableSortDirection,
} from '@/store/api/userApi';
import type { SelectChangeEvent } from '@/ui';
import type { FC, ReactNode } from 'react';

import { useStickyColumnHeader } from '@/features/companyDashboard/common/members/useStickyColumnHeader';

export interface MembersTableProps {
  businessPlanFilter: string[];
  coachesAssignedFilter: MembersTableCoachCountFilter[];
  coachFilter: string[];
  cohortFilter?: string[];
  cohortOptions?: FilterOption[];
  columns?: MembersTableColumn[];
  companyFilter: string[];
  enableRowNavigation?: boolean;
  endDateStatusFilter: string[];
  error?: boolean;
  growthPlanStartedFilter: string[];
  facets: MembersTableFacets;
  filters?: MembersTableFilterKey[];
  // Rendered at the end of the title row, e.g. a Download CSV button.
  headerActions?: ReactNode;
  insightsStatusFilter: string[];
  isFetching?: boolean;
  // Rendered first in the filter row; "Clear all" neither counts nor clears them.
  leadingFilters?: ReactNode;
  loading?: boolean;
  memberStatusFilter: string[];
  rowHref?: (row: MemberTableRow) => string | null;
  sessionFilter: string[];
  onBusinessPlanFilterChange: (selected: string[]) => void;
  onClearAllFilters: () => void;
  onCoachesAssignedFilterChange: (
    selected: MembersTableCoachCountFilter[],
  ) => void;
  onCoachFilterChange: (selected: string[]) => void;
  onCohortFilterChange?: (selected: string[]) => void;
  onCompanyFilterChange: (selected: string[]) => void;
  onEndDateStatusFilterChange: (selected: string[]) => void;
  onGrowthPlanStartedFilterChange: (selected: string[]) => void;
  onInsightsStatusFilterChange: (selected: string[]) => void;
  onMemberStatusFilterChange: (selected: string[]) => void;
  onSessionFilterChange: (selected: string[]) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearchChange: (search: string) => void;
  onSortChange: (field: MembersTableSortBy) => void;
  onSeatTypeFilterChange: (selected: string[]) => void;
  onAdminNoteSave?: (userId: string, adminNote: string) => Promise<void> | void;
  page: number;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  rows: MemberTableRow[];
  searchQuery: string;
  seatTypeFilter: string[];
  showCoachFilter?: boolean;
  showFilterBar?: boolean;
  showSearch?: boolean;
  sortDirection: MembersTableSortDirection;
  sortField: MembersTableSortBy;
  // Pins the title, filters and column headings to the top of the page while it scrolls. The
  // title then carries the count itself, e.g. "Members (42)".
  stickyHeader?: boolean;
  title?: string;
  // Page-level tables use h4 to match other page titles; embedded tables keep h5.
  titleVariant?: 'h4' | 'h5';
  total: number;
  totalPages: number;
}

const EMPTY_FACETS: MembersTableFacets = {
  businessPlans: [],
  coaches: [],
  coachesAssigned: [],
  companies: [],
  endDateStatuses: [],
  growthPlanStarted: [],
  insightsStatuses: [],
  memberStatuses: [],
  seatTypes: [],
  sessionFilters: [],
};

export const emptyMembersTableFacets = EMPTY_FACETS;

const endDateStatusOptions = (statuses: string[]): FilterOption[] =>
  statuses.map((status) => ({ id: status, name: status }));

type PaginationItem = number | 'start-ellipsis' | 'end-ellipsis';

const paginationItems = (currentPage: number, totalPages: number) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  let start = Math.max(1, currentPage - 1);
  let end = Math.min(totalPages - 2, currentPage + 1);

  if (currentPage <= 2) {
    end = 3;
  }
  if (currentPage >= totalPages - 3) {
    start = totalPages - 4;
  }

  const items: PaginationItem[] = [0];

  if (start > 1) {
    items.push('start-ellipsis');
  } else {
    for (let pageIndex = 1; pageIndex < start; pageIndex += 1) {
      items.push(pageIndex);
    }
  }

  for (let pageIndex = start; pageIndex <= end; pageIndex += 1) {
    items.push(pageIndex);
  }

  if (end < totalPages - 2) {
    items.push('end-ellipsis');
  } else {
    for (let pageIndex = end + 1; pageIndex < totalPages - 1; pageIndex += 1) {
      items.push(pageIndex);
    }
  }

  items.push(totalPages - 1);

  return items;
};

const filterCountByKey = (
  filter: MembersTableFilterKey,
  values: {
    businessPlanFilter: string[];
    coachesAssignedFilter: MembersTableCoachCountFilter[];
    coachFilter: string[];
    cohortFilter: string[];
    companyFilter: string[];
    endDateStatusFilter: string[];
    growthPlanStartedFilter: string[];
    insightsStatusFilter: string[];
    memberStatusFilter: string[];
    seatTypeFilter: string[];
    sessionFilter: string[];
  },
) => {
  switch (filter) {
    case 'company':
      return values.companyFilter.length;
    case 'cohort':
      return values.cohortFilter.length;
    case 'memberStatus':
      return values.memberStatusFilter.length;
    case 'businessPlan':
      return values.businessPlanFilter.length;
    case 'coach':
      return values.coachFilter.length;
    case 'endDateStatus':
      return values.endDateStatusFilter.length;
    case 'coachesAssigned':
      return values.coachesAssignedFilter.length;
    case 'growthPlanStarted':
      return values.growthPlanStartedFilter.length;
    case 'insightsStatus':
      return values.insightsStatusFilter.length;
    case 'seatType':
      return values.seatTypeFilter.length;
    case 'sessions':
      return values.sessionFilter.length;
    default:
      return 0;
  }
};

const MembersTable: FC<MembersTableProps> = ({
  businessPlanFilter,
  coachesAssignedFilter,
  coachFilter,
  cohortFilter = [],
  cohortOptions = [],
  columns = ALL_MEMBERS_TABLE_COLUMNS,
  companyFilter,
  enableRowNavigation = true,
  endDateStatusFilter,
  error = false,
  growthPlanStartedFilter,
  facets,
  filters = ALL_MEMBERS_TABLE_FILTERS,
  headerActions,
  insightsStatusFilter,
  isFetching = false,
  leadingFilters,
  loading = false,
  memberStatusFilter,
  rowHref,
  sessionFilter,
  onAdminNoteSave,
  onBusinessPlanFilterChange,
  onClearAllFilters,
  onCoachesAssignedFilterChange,
  onCoachFilterChange,
  onCohortFilterChange = () => undefined,
  onCompanyFilterChange,
  onEndDateStatusFilterChange,
  onGrowthPlanStartedFilterChange,
  onInsightsStatusFilterChange,
  onMemberStatusFilterChange,
  onSessionFilterChange,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onSeatTypeFilterChange,
  onSortChange,
  page,
  pageSize,
  pageSizeOptions = [],
  rows,
  searchQuery,
  seatTypeFilter,
  showCoachFilter = true,
  showFilterBar = true,
  showSearch = true,
  sortDirection,
  sortField,
  stickyHeader = false,
  title = 'All Members',
  titleVariant = 'h5',
  total,
  totalPages,
  // eslint-disable-next-line complexity -- table shell coordinates several optional filters/columns.
}) => {
  const visibleFilters = filters.filter(
    (filter) => filter !== 'coach' || showCoachFilter,
  );
  const activeFilterCount = visibleFilters.filter(
    (filter) =>
      filterCountByKey(filter, {
        businessPlanFilter,
        coachFilter,
        coachesAssignedFilter,
        cohortFilter,
        companyFilter,
        endDateStatusFilter,
        growthPlanStartedFilter,
        insightsStatusFilter,
        memberStatusFilter,
        seatTypeFilter,
        sessionFilter,
      }) > 0,
  ).length;
  const hasActiveSearch = searchQuery.trim().length > 0;

  const pageNumbers = paginationItems(page, totalPages);
  const emptyMessage = error
    ? 'Members could not be loaded. Please try again.'
    : 'No members match the selected filters.';
  const showSearchAndFilters =
    showSearch || (showFilterBar && visibleFilters.length > 0);
  const showPageSizeControl = Boolean(
    onPageSizeChange && pageSizeOptions.length > 0 && pageSize,
  );
  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onPageSizeChange?.(event.target.value as number);
  };

  const { bodyScrollerRef, stripRef } = useStickyColumnHeader();

  const headerCells = columns.map((col) => (
    <TableCell
      key={col.key}
      className="!px-3 !py-2 !text-xs !font-semibold !uppercase !tracking-wider !text-cement-500"
      style={
        col.sortable ? { cursor: 'pointer', userSelect: 'none' } : undefined
      }
      onClick={
        col.sortable
          ? () => onSortChange(col.key as MembersTableSortBy)
          : undefined
      }
    >
      <div className="flex items-center gap-1">
        {col.label}
        {col.sortable && sortField === col.key && (
          <span className="material-icons text-sm">
            {sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}
          </span>
        )}
      </div>
    </TableCell>
  ));

  const titleRow = title && (
    <div className="flex items-baseline gap-2">
      <Typography
        className="!font-bold !text-cement-500"
        variant={titleVariant}
      >
        {title}
      </Typography>
      {!stickyHeader && (
        <Typography color="text.secondary" variant="body2">
          {total.toLocaleString()}
        </Typography>
      )}
      {isFetching && !loading && (
        <Typography color="text.secondary" variant="caption">
          Refreshing…
        </Typography>
      )}
      {headerActions && <div className="ml-auto">{headerActions}</div>}
    </div>
  );

  const filterRow = showSearchAndFilters && (
    <div
      className={clsx('flex items-center gap-3', {
        // The pinned company header has more filters than fit one row; wrap rather than squeeze them.
        'flex-wrap': stickyHeader,
      })}
    >
      {showSearch && (
        <div className={clsx('relative w-80', { 'shrink-0': stickyHeader })}>
          <input
            className="w-full rounded-full bg-white py-2 pl-4 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-cement-400"
            placeholder="Search"
            style={{ border: '1px solid #A4A4A4' }}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <span className="material-icons pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xl text-cement-400">
            search
          </span>
        </div>
      )}
      {showFilterBar && visibleFilters.length > 0 && (
        <>
          <span className="material-icons text-lg text-cement-400">
            filter_list
          </span>
          {leadingFilters}
          {visibleFilters.includes('company') && (
            <FilterDropdown
              label="Company"
              options={facets.companies}
              selected={companyFilter}
              onChange={onCompanyFilterChange}
            />
          )}
          {visibleFilters.includes('memberStatus') && (
            <FilterDropdown
              label="Status"
              options={facets.memberStatuses}
              selected={memberStatusFilter}
              onChange={onMemberStatusFilterChange}
            />
          )}
          {visibleFilters.includes('businessPlan') && (
            <FilterDropdown
              label="Business Plan"
              options={facets.businessPlans}
              selected={businessPlanFilter}
              onChange={onBusinessPlanFilterChange}
            />
          )}
          {visibleFilters.includes('coach') && (
            <FilterDropdown
              label="Coach"
              options={facets.coaches}
              selected={coachFilter}
              onChange={onCoachFilterChange}
            />
          )}
          {visibleFilters.includes('endDateStatus') && (
            <FilterDropdown
              label="End Date Status"
              options={endDateStatusOptions(facets.endDateStatuses)}
              selected={endDateStatusFilter}
              onChange={onEndDateStatusFilterChange}
            />
          )}
          {visibleFilters.includes('coachesAssigned') && (
            <FilterDropdown
              label="Coaches Assigned"
              options={facets.coachesAssigned}
              selected={coachesAssignedFilter}
              onChange={(selected) =>
                onCoachesAssignedFilterChange(
                  selected as MembersTableCoachCountFilter[],
                )
              }
            />
          )}
          {visibleFilters.includes('growthPlanStarted') && (
            <FilterDropdown
              label="Growth Plan Started"
              options={facets.growthPlanStarted}
              selected={growthPlanStartedFilter}
              onChange={onGrowthPlanStartedFilterChange}
            />
          )}
          {visibleFilters.includes('insightsStatus') && (
            <FilterDropdown
              label="Insights Status"
              options={facets.insightsStatuses}
              selected={insightsStatusFilter}
              onChange={onInsightsStatusFilterChange}
            />
          )}
          {visibleFilters.includes('seatType') && (
            <FilterDropdown
              label="Seat Type"
              options={facets.seatTypes}
              selected={seatTypeFilter}
              onChange={onSeatTypeFilterChange}
            />
          )}
          {visibleFilters.includes('cohort') && (
            <FilterDropdown
              label="Cohort"
              options={[{ id: 'none', name: 'None' }, ...cohortOptions]}
              selected={cohortFilter}
              onChange={onCohortFilterChange}
            />
          )}
          {visibleFilters.includes('sessions') && (
            <FilterDropdown
              label="Sessions"
              options={facets.sessionFilters}
              selected={sessionFilter}
              onChange={onSessionFilterChange}
            />
          )}
        </>
      )}
      {(activeFilterCount > 0 || hasActiveSearch) && (
        <button
          className="ml-1 text-xs text-cement-400 underline hover:text-cement-500"
          type="button"
          onClick={onClearAllFilters}
        >
          Clear all
        </button>
      )}
    </div>
  );

  return (
    <div className={clsx('flex flex-col', { 'gap-4': !stickyHeader })}>
      {stickyHeader ? (
        // Pinned to <main>, so it must stay outside every overflow wrapper.
        <div className="sticky top-0 z-10 flex flex-col gap-4 bg-white pt-4">
          {titleRow}
          {filterRow}
          <div
            ref={stripRef}
            className="overflow-hidden rounded-t-xl border border-b-0 border-cement-200 bg-white"
          >
            <TableContainer className="!overflow-visible">
              <Table size="small" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  {columns.map((col) => (
                    <col key={col.key} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow className="bg-cement-100">{headerCells}</TableRow>
                </TableHead>
              </Table>
            </TableContainer>
          </div>
        </div>
      ) : (
        <>
          {titleRow}
          {filterRow}
        </>
      )}

      {/* Table */}
      <div
        className={clsx('overflow-hidden border border-cement-200 bg-white', {
          'rounded-b-xl border-t-0': stickyHeader,
          'rounded-xl': !stickyHeader,
        })}
      >
        {loading && <LinearProgress />}
        <div
          ref={stickyHeader ? bodyScrollerRef : undefined}
          className="overflow-x-auto"
        >
          <TableContainer
            className={clsx({ '!overflow-visible': stickyHeader })}
          >
            <Table size="small">
              <TableHead>
                {/* With a sticky header, this row only sizes the columns; the strip above shows
                    the headings. */}
                <TableRow
                  aria-hidden={stickyHeader || undefined}
                  className={clsx('bg-cement-100', { collapse: stickyHeader })}
                >
                  {headerCells}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <MembersTableRowComponent
                    key={row.user.id}
                    columns={columns}
                    enableRowNavigation={enableRowNavigation}
                    isAlt={index % 2 === 1}
                    row={row}
                    rowHref={rowHref}
                    onAdminNoteSave={onAdminNoteSave}
                  />
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      className="!py-12 !text-center"
                      colSpan={columns.length}
                    >
                      <Typography color="text.secondary" variant="body1">
                        {emptyMessage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Pagination */}
      {(totalPages > 1 || showPageSizeControl) && (
        <div
          className={clsx('flex items-center justify-between gap-3 py-2', {
            'mt-4': stickyHeader,
          })}
        >
          <div>
            {showPageSizeControl && (
              <FormControl className="min-w-32" size="small">
                <InputLabel id="members-page-size-select-label">
                  Per page
                </InputLabel>
                <Select
                  id="members-page-size-select"
                  label="Per page"
                  labelId="members-page-size-select-label"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {pageSizeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button
                className="cursor-pointer border-none bg-transparent text-sm text-cement-400 disabled:cursor-not-allowed disabled:opacity-30"
                disabled={page === 0}
                type="button"
                onClick={() => onPageChange(0)}
              >
                &#x226A;
              </button>
              <button
                className="cursor-pointer border-none bg-transparent text-sm text-cement-400 disabled:cursor-not-allowed disabled:opacity-30"
                disabled={page === 0}
                type="button"
                onClick={() => onPageChange(page - 1)}
              >
                &#x2039;
              </button>
              {pageNumbers.map((p) =>
                typeof p === 'number' ? (
                  <button
                    key={p}
                    className={`flex size-7 cursor-pointer items-center justify-center border-none text-sm ${
                      p === page
                        ? 'rounded-full font-semibold text-cement-600'
                        : 'bg-transparent text-cement-400'
                    }`}
                    style={
                      p === page ? { backgroundColor: '#EDF2F0' } : undefined
                    }
                    type="button"
                    onClick={() => onPageChange(p)}
                  >
                    {p + 1}
                  </button>
                ) : (
                  <span
                    key={p}
                    className="flex size-7 items-center justify-center text-sm text-cement-400"
                  >
                    …
                  </span>
                ),
              )}
              <button
                className="cursor-pointer border-none bg-transparent text-sm text-cement-400 disabled:cursor-not-allowed disabled:opacity-30"
                disabled={page >= totalPages - 1}
                type="button"
                onClick={() => onPageChange(page + 1)}
              >
                &#x203A;
              </button>
              <button
                className="cursor-pointer border-none bg-transparent text-sm text-cement-400 disabled:cursor-not-allowed disabled:opacity-30"
                disabled={page >= totalPages - 1}
                type="button"
                onClick={() => onPageChange(totalPages - 1)}
              >
                &#x226B;
              </button>
            </div>
          )}
          <div />
        </div>
      )}
    </div>
  );
};

export default MembersTable;
